// handlers/rolesPageHandlers.ts

import {
  deleteRol,
  getListadoRoles,
  postRol,
  PostRolProps,
  putRol,
  PutRolProps,
} from "@/services/users-bo.service";
import { toaster } from "@/components/ui/toaster";
import { FieldValues, UseFormSetValue } from "react-hook-form";
import { handlePermissionError } from "@/utils/permission-check";
import { NavigateFunction } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";
import { Dispatch, SetStateAction } from "react";
import { AxiosError } from "axios";
import { getRLFilterRequest, RLFProps } from "./filter-obj";

interface DeleteRoleHandlerProps {
  selection: string[];
  setLoadingDeleteRol: (val: boolean) => void;
  setOpenConfirm: (val: boolean) => void;
  fetchRoles: (filters: any, showLoad?: boolean) => Promise<void>;
  currentFilters: any;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const handleDeleteRoleExternal = async ({
  selection,
  setLoadingDeleteRol,
  setOpenConfirm,
  fetchRoles,
  currentFilters,
  navigate,
  fetchPermissions,
}: DeleteRoleHandlerProps) => {
  try {
    setLoadingDeleteRol(true);

    await deleteRol({
      ids: selection.map(Number),
    });

    toaster.create({
      title: "Roles seleccionados eliminados con éxito.",
      type: "success",
    });
  } catch (error) {
    const errorObj = (error as any)?.response?.data;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.ROLES,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.message ??
          "No se ha podido eliminar los roles seleccionados.",
      });
    }
    throw error;
  } finally {
    setLoadingDeleteRol(false);
    setOpenConfirm(false);
    await fetchRoles(currentFilters, false);
  }
};

type HandleCreateRoleProps = {
  data: FieldValues;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  setLoading: (state: boolean) => void;
  redirectPath: string;
};

export const handleCreateRole = async ({
  data,
  navigate,
  fetchPermissions,
  setLoading,
  redirectPath,
}: HandleCreateRoleProps) => {
  try {
    setLoading(true);

    const objToSend: PostRolProps = {
      nombre: data.name,
      idPermisos: data.permissions,
    };

    await postRol(objToSend);
    await fetchPermissions();

    toaster.create({
      title: "Rol creado con éxito.",
      type: "success",
    });

    navigate(redirectPath);
  } catch (error) {
    const errorMessage = (error as { response: any })?.response?.data?.message;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.ROLES,
    );
    if (!handled) {
      toaster.create({
        title: errorMessage ?? "No se pudo crear el rol.",
        type: "error",
      });
    }
    throw error;
  } finally {
    setLoading(false);
  }
};

type HandleUpdateRoleProps = {
  data: FieldValues;
  roleId: number;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  setLoading: (state: boolean) => void;
  redirectPath: string;
};

export const handleUpdateRole = async ({
  data,
  roleId,
  navigate,
  fetchPermissions,
  setLoading,
  redirectPath,
}: HandleUpdateRoleProps) => {
  try {
    setLoading(true);

    const objToSend: PutRolProps = {
      id: roleId,
      nombre: data.name,
      idPermisos: data.permissions,
    };

    await putRol(objToSend);
    await fetchPermissions();

    toaster.create({
      title: "Rol modificado con éxito.",
      type: "success",
    });

    navigate(redirectPath);
  } catch (error) {
    const errorMessage = (error as { response: any })?.response?.data?.message;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.ROLES,
    );
    if (!handled) {
      toaster.create({
        title: errorMessage ?? "No se pudo modificar el rol.",
        type: "error",
      });
    }
    throw error;
  } finally {
    setLoading(false);
  }
};

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<{ name?: string; status?: boolean }>;
  setFiltersApplied: (val: boolean) => void;
}

export const clearFiltersHandler = ({
  setValue,
  setFiltersApplied,
}: ClearFiltersHandlerProps) => {
  setValue("name", "");
  setFiltersApplied(false);
};

interface SubmitFiltersHandlerProps {
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  fetchRoles: (filters: any, showLoad?: boolean) => Promise<void>;
  event: FieldValues;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const submitFiltersHandler = async ({
  setFiltersApplied,
  setCurrentFilters,
  setOpenFilters,
  fetchRoles,
  event,
  navigate,
  fetchPermissions,
}: SubmitFiltersHandlerProps) => {
  try {
    setFiltersApplied(true);
    const filterObj = getRLFilterRequest(event as RLFProps);

    setCurrentFilters(filterObj);
    if (!Object.keys(filterObj)?.length) setFiltersApplied(false);
    await fetchRoles(filterObj);

    setOpenFilters(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorObj?.message ?? "No se pudo aplicar los filtros",
      });
    }
    throw error;
  }
};

interface FetchRolesHandlerProps {
  filters: any;
  showLoad: boolean;
  setLoadingRoles: (val: boolean) => void;
  setCurrentFilters: (val: any) => void;
  setAvailableRoles: (val: any) => void;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchRolesHandler = async ({
  filters,
  showLoad,
  setLoadingRoles,
  setCurrentFilters,
  setAvailableRoles,
  navigate,
  fetchPermissions,
}: FetchRolesHandlerProps) => {
  try {
    if (showLoad) setLoadingRoles(true);

    const response = await getListadoRoles({
      ...filters,
    });

    setCurrentFilters({
      ...filters,
    });

    setAvailableRoles(response.listado);

    setLoadingRoles(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.ROLES,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudo cargar el listado de roles.",
      });
    }
  }
};
